import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import EnvelopesModal from "@/components/EnvelopesModal";
import { CAPTURE } from "@/constants/testIds";
import { toast, softDeleteWithUndo } from "@/lib/notify";
import { Trash2, Plus, Save, Wand2, Mail, Lock } from "lucide-react";

const MONTHS = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const DAY_NAMES = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

const now = new Date();

const fmt = (n) =>
  Number(n || 0).toLocaleString("es-MX", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const parseISODate = (iso) => {
  // YYYY-MM-DD -> Date in local time without TZ surprises
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const isoOf = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const inferServiceType = (iso) => {
  const dow = parseISODate(iso).getDay();
  if (dow === 0) return "Domingo";
  if (dow === 4) return "Jueves";
  return "Especial";
};

const EMPTY_ROW = (date = isoOf(now)) => ({
  id: null,
  _tmpId: `tmp-${Math.random().toString(36).slice(2)}`,
  date,
  service_type: inferServiceType(date),
  tithes: 0,
  offering: 0,
  special_offering: 0,
  attendance_adults: 0,
  attendance_youth: 0,
  attendance_kids: 0,
  notes: "",
  _dirty: true,
});

export default function Captura() {
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState([]);
  const [envelopeRow, setEnvelopeRow] = useState(null);
  // Map of capture_id -> { Diezmo: number, Ofrenda: number } from envelopes
  const [envSums, setEnvSums] = useState({});

  const years = useMemo(() => {
    const y = now.getFullYear();
    return [y - 2, y - 1, y, y + 1];
  }, []);

  const loadEnvelopeSums = useCallback(async (y, m) => {
    try {
      const { data } = await api.get("/envelopes", { params: { year: y, month: m } });
      const next = {};
      for (const e of data) {
        const cid = e.capture_id;
        if (!cid) continue;
        if (!next[cid]) next[cid] = { Diezmo: 0, Ofrenda: 0 };
        next[cid][e.type] = (next[cid][e.type] || 0) + (Number(e.amount) || 0);
      }
      setEnvSums(next);
    } catch {
      setEnvSums({});
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/captures", { params: { year, month } });
      const mapped = data.map((d) => ({ ...d, _dirty: false, _tmpId: null }));
      mapped.sort((a, b) => (a.date < b.date ? -1 : 1));
      setRows(mapped);
      await loadEnvelopeSums(year, month);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando datos");
    } finally {
      setLoading(false);
    }
  }, [year, month, loadEnvelopeSums]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api
      .get("/members", { params: { status: "Activo" } })
      .then(({ data }) => setMembers(data))
      .catch(() => setMembers([]));
  }, []);

  // Compute displayed tithes for a row: live envelope sum if row saved, else stored tithes
  const displayedTithes = useCallback(
    (r) => (r.id && envSums[r.id] ? envSums[r.id].Diezmo : Number(r.tithes) || 0),
    [envSums]
  );

  const generateTemplate = () => {
    const existing = new Set(rows.map((r) => r.date));
    const days = new Date(year, month, 0).getDate();
    const additions = [];
    for (let d = 1; d <= days; d++) {
      const dt = new Date(year, month - 1, d);
      const dow = dt.getDay();
      if (dow !== 0 && dow !== 4) continue;
      const iso = isoOf(dt);
      if (existing.has(iso)) continue;
      additions.push({ ...EMPTY_ROW(iso), service_type: dow === 0 ? "Domingo" : "Jueves" });
    }
    if (!additions.length) {
      toast.info("Ya están todos los domingos y jueves del mes.");
      return;
    }
    const merged = [...rows, ...additions].sort((a, b) => (a.date < b.date ? -1 : 1));
    setRows(merged);
  };

  const addEmptyRow = () => {
    const iso = isoOf(new Date(year, month - 1, 1));
    const merged = [...rows, EMPTY_ROW(iso)].sort((a, b) => (a.date < b.date ? -1 : 1));
    setRows(merged);
  };

  const updateRow = (key, field, value) => {
    setRows((prev) =>
      prev.map((r) => {
        if ((r.id || r._tmpId) !== key) return r;
        const updated = { ...r, [field]: value, _dirty: true };
        if (field === "date") updated.service_type = inferServiceType(value);
        return updated;
      })
    );
  };

  const applyEnvelopeTotal = async ({ kind, value }) => {
    if (!envelopeRow?.id) return;
    const field = kind === "tithes" ? "tithes" : "offering";
    const newVal = Number(value) || 0;
    const row = rows.find((r) => r.id === envelopeRow.id);
    if (!row) return;
    const payload = {
      date: row.date,
      service_type: row.service_type,
      tithes: Number(row.tithes) || 0,
      offering: Number(row.offering) || 0,
      special_offering: Number(row.special_offering) || 0,
      attendance_adults: Number(row.attendance_adults) || 0,
      attendance_youth: Number(row.attendance_youth) || 0,
      attendance_kids: Number(row.attendance_kids) || 0,
      notes: row.notes || "",
    };
    payload[field] = newVal;
    const { data } = await api.put(`/captures/${row.id}`, payload);
    setRows((prev) =>
      prev.map((r) =>
        r.id === row.id ? { ...data, _dirty: false, _tmpId: null } : r
      )
    );
    setEnvelopeRow((er) => (er && er.id === row.id ? { ...er, ...data } : er));
    toast.success(
      kind === "tithes"
        ? `Diezmos aplicados: $${fmt(newVal)}`
        : `Ofrendas aplicadas: $${fmt(newVal)}`,
    );
  };

  // When envelopes modal closes, refresh envelope sums and sync tithes silently if changed
  const handleEnvelopesClose = async () => {
    const closingRow = envelopeRow;
    setEnvelopeRow(null);
    if (!closingRow?.id) return;
    try {
      const { data } = await api.get("/envelopes", { params: { capture_id: closingRow.id } });
      const sum = data
        .filter((e) => e.type === "Diezmo")
        .reduce((acc, e) => acc + (Number(e.amount) || 0), 0);
      const offSum = data
        .filter((e) => e.type === "Ofrenda")
        .reduce((acc, e) => acc + (Number(e.amount) || 0), 0);
      setEnvSums((prev) => ({
        ...prev,
        [closingRow.id]: { Diezmo: sum, Ofrenda: offSum },
      }));
      // Auto-sync tithes to backend if differs from stored value
      const current = rows.find((r) => r.id === closingRow.id);
      if (current && Math.abs((Number(current.tithes) || 0) - sum) > 0.005) {
        const payload = {
          date: current.date,
          service_type: current.service_type,
          tithes: sum,
          offering: Number(current.offering) || 0,
          special_offering: Number(current.special_offering) || 0,
          attendance_adults: Number(current.attendance_adults) || 0,
          attendance_youth: Number(current.attendance_youth) || 0,
          attendance_kids: Number(current.attendance_kids) || 0,
          notes: current.notes || "",
        };
        const { data: saved } = await api.put(`/captures/${current.id}`, payload);
        setRows((prev) =>
          prev.map((r) => (r.id === current.id ? { ...saved, _dirty: false, _tmpId: null } : r))
        );
      }
    } catch (e) {
      // Non-blocking autosave failure — the user can press Guardar to retry.
      console.warn("Captura autosave failed", e);
    }
  };

  const removeRow = (row) => {
    const key = row.id || row._tmpId;
    // Optimistically remove from UI
    setRows((prev) => prev.filter((r) => (r.id || r._tmpId) !== key));
    // Unsaved rows have no server-side delete to schedule
    if (!row.id) return;
    softDeleteWithUndo({
      message: "Captura eliminada",
      description: `${row.date} · ${row.service_type}`,
      onUndo: () => {
        setRows((prev) => {
          if (prev.some((r) => r.id === row.id)) return prev;
          return [...prev, row].sort((a, b) => (a.date < b.date ? -1 : 1));
        });
      },
      onDelete: async () => {
        try {
          await api.delete(`/captures/${row.id}`);
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Error eliminando — restaurando");
          setRows((prev) => {
            if (prev.some((r) => r.id === row.id)) return prev;
            return [...prev, row].sort((a, b) => (a.date < b.date ? -1 : 1));
          });
        }
      },
    });
  };

  const saveAll = async () => {
    const dirty = rows.filter((r) => r._dirty);
    if (!dirty.length) {
      toast.info("Sin cambios para guardar.");
      return;
    }
    setSaving(true);
    try {
      const updated = [...rows];
      for (const row of dirty) {
        // Tithes are read-only / auto-derived from envelopes when row already saved.
        const tithesToSave = row.id && envSums[row.id]
          ? envSums[row.id].Diezmo
          : Number(row.tithes) || 0;
        const payload = {
          date: row.date,
          service_type: row.service_type,
          tithes: tithesToSave,
          offering: Number(row.offering) || 0,
          special_offering: Number(row.special_offering) || 0,
          attendance_adults: Number(row.attendance_adults) || 0,
          attendance_youth: Number(row.attendance_youth) || 0,
          attendance_kids: Number(row.attendance_kids) || 0,
          notes: row.notes || "",
        };
        let saved;
        if (row.id) {
          const { data } = await api.put(`/captures/${row.id}`, payload);
          saved = data;
        } else {
          const { data } = await api.post("/captures", payload);
          saved = data;
        }
        const idx = updated.findIndex(
          (r) => (r.id || r._tmpId) === (row.id || row._tmpId)
        );
        if (idx !== -1) updated[idx] = { ...saved, _dirty: false, _tmpId: null };
      }
      setRows(updated);
      toast.success(
        `Guardado: ${dirty.length} registro${dirty.length === 1 ? "" : "s"}.`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.tithes += displayedTithes(r);
        acc.offering += Number(r.offering) || 0;
        acc.special_offering += Number(r.special_offering) || 0;
        acc.attendance_adults += Number(r.attendance_adults) || 0;
        acc.attendance_youth += Number(r.attendance_youth) || 0;
        acc.attendance_kids += Number(r.attendance_kids) || 0;
        return acc;
      },
      {
        tithes: 0,
        offering: 0,
        special_offering: 0,
        attendance_adults: 0,
        attendance_youth: 0,
        attendance_kids: 0,
      }
    );
  }, [rows, displayedTithes]);

  const totalEntradas =
    totals.tithes + totals.offering + totals.special_offering;
  const totalAsistencia =
    totals.attendance_adults + totals.attendance_youth + totals.attendance_kids;
  const dirtyCount = rows.filter((r) => r._dirty).length;

  return (
    <div data-testid={CAPTURE.page}>
      <PageHeader
        title="Captura Masiva"
        subtitle="Ofrendas, diezmos y asistencia · Hoja semanal estilo libro contable"
        actions={
          <div className="flex items-center gap-2">
            <button
              data-testid={CAPTURE.addRowButton}
              onClick={addEmptyRow}
              className="flex items-center gap-2 border border-[#d9cfb8] bg-white text-[#5d5849] hover:bg-[#fbf7ee] px-4 py-2.5 text-xs uppercase tracking-widest"
            >
              <Plus size={14} /> Fila
            </button>
            <button
              onClick={generateTemplate}
              className="flex items-center gap-2 border border-[#d9cfb8] bg-white text-[#5d5849] hover:bg-[#fbf7ee] px-4 py-2.5 text-xs uppercase tracking-widest"
            >
              <Wand2 size={14} /> Generar mes
            </button>
            <button
              data-testid={CAPTURE.saveAllButton}
              onClick={saveAll}
              disabled={saving}
              className="flex items-center gap-2 bg-[#1c2a3a] hover:bg-[#0f1a2c] text-[#e7c879] px-5 py-2.5 text-xs uppercase tracking-widest disabled:opacity-60"
            >
              <Save size={14} />
              {saving ? "Guardando..." : `Guardar ${dirtyCount ? `(${dirtyCount})` : ""}`}
            </button>
          </div>
        }
      />

      <div className="p-8 space-y-4">
        {/* Filters */}
        <div
          className="bg-white border border-[#d9cfb8] px-5 py-4 flex flex-wrap items-end gap-6"
          style={{ borderTop: "3px solid #c8a04a" }}
        >
          <Field label="Mes">
            <select
              data-testid={CAPTURE.monthSelect}
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className={selectCls}
            >
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>{m}</option>
              ))}
            </select>
          </Field>
          <Field label="Año">
            <select
              data-testid={CAPTURE.yearSelect}
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className={selectCls}
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </Field>

          <div className="ml-auto flex items-center gap-6 text-right">
            <Stat label="Total Entradas" value={`$ ${fmt(totalEntradas)}`} accent="#3f7c2a" />
            <Stat label="Asistencia" value={totalAsistencia} accent="#1c2a3a" />
            <Stat label="Registros" value={rows.length} accent="#c8a04a" />
          </div>
        </div>

        {/* Spreadsheet */}
        <div className="overflow-x-auto border border-[#d9cfb8] bg-white">
          <table className="ajar-spreadsheet" data-testid={CAPTURE.table}>
            <thead>
              <tr>
                <th style={{ width: 110 }}>Fecha</th>
                <th style={{ width: 50 }}>Día</th>
                <th style={{ width: 110 }}>Servicio</th>
                <th className="text-right" style={{ width: 120 }}>Diezmos</th>
                <th className="text-right" style={{ width: 130 }}>Ofrenda</th>
                <th className="text-right" style={{ width: 130 }}>Of. Especial</th>
                <th className="text-right" style={{ width: 80 }}>Adultos</th>
                <th className="text-right" style={{ width: 80 }}>Jóvenes</th>
                <th className="text-right" style={{ width: 80 }}>Niños</th>
                <th className="text-right" style={{ width: 110 }}>Total</th>
                <th>Notas</th>
                <th style={{ width: 60 }}>Sobres</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={13} className="text-center py-10 text-[#8b7e5e]">
                    Cargando...
                  </td>
                </tr>
              )}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={13} className="text-center py-10 text-[#8b7e5e]">
                    Sin registros para {MONTHS[month - 1]} {year}. Usa{" "}
                    <strong>Generar mes</strong> o <strong>Fila</strong> para empezar.
                  </td>
                </tr>
              )}
              {!loading &&
                rows.map((r) => {
                  const key = r.id || r._tmpId;
                  const dow = parseISODate(r.date).getDay();
                  const rowCls =
                    dow === 0 ? "ajar-row-sunday" : dow === 4 ? "ajar-row-thursday" : "";
                  const tithesVal = displayedTithes(r);
                  const total =
                    tithesVal +
                    (Number(r.offering) || 0) +
                    (Number(r.special_offering) || 0);
                  return (
                    <tr key={key} data-testid={CAPTURE.row(key)} className={rowCls}>
                      <td>
                        <input
                          type="date"
                          value={r.date}
                          onChange={(e) => updateRow(key, "date", e.target.value)}
                          className="ajar-input"
                        />
                      </td>
                      <td className="text-center text-xs text-[#5d5849]">{DAY_NAMES[dow]}</td>
                      <td>
                        <select
                          value={r.service_type}
                          onChange={(e) => updateRow(key, "service_type", e.target.value)}
                          className="ajar-input"
                        >
                          <option value="Domingo">Domingo</option>
                          <option value="Jueves">Jueves</option>
                          <option value="Especial">Especial</option>
                        </select>
                      </td>
                      <TithesCell
                        testid={CAPTURE.tithesInput(key)}
                        value={tithesVal}
                        hasId={!!r.id}
                      />
                      <CurrencyCell
                        testid={CAPTURE.offeringInput(key)}
                        value={r.offering}
                        onChange={(v) => updateRow(key, "offering", v)}
                      />
                      <CurrencyCell
                        testid={CAPTURE.specialInput(key)}
                        value={r.special_offering}
                        onChange={(v) => updateRow(key, "special_offering", v)}
                      />
                      <NumCell
                        testid={CAPTURE.adultsInput(key)}
                        value={r.attendance_adults}
                        integer
                        onChange={(v) => updateRow(key, "attendance_adults", v)}
                      />
                      <NumCell
                        testid={CAPTURE.youthInput(key)}
                        value={r.attendance_youth}
                        integer
                        onChange={(v) => updateRow(key, "attendance_youth", v)}
                      />
                      <NumCell
                        testid={CAPTURE.kidsInput(key)}
                        value={r.attendance_kids}
                        integer
                        onChange={(v) => updateRow(key, "attendance_kids", v)}
                      />
                      <td className="text-right tabular-nums font-medium">
                        $ {fmt(total)}
                      </td>
                      <td>
                        <input
                          data-testid={CAPTURE.notesInput(key)}
                          value={r.notes || ""}
                          onChange={(e) => updateRow(key, "notes", e.target.value)}
                          className="ajar-input"
                          placeholder="—"
                        />
                      </td>
                      <td className="text-center">
                        {r.id ? (
                          <button
                            data-testid={CAPTURE.envelopesButton(r.id)}
                            onClick={() => setEnvelopeRow(r)}
                            className="text-[#1c2a3a] hover:text-[#c8a04a]"
                            title="Sobres del servicio (define los Diezmos)"
                          >
                            <Mail size={14} />
                          </button>
                        ) : (
                          <span className="text-[10px] text-[#8b7e5e]">guarda</span>
                        )}
                      </td>
                      <td className="text-center">
                        <button
                          data-testid={CAPTURE.deleteButton(key)}
                          onClick={() => removeRow(r)}
                          className="text-[#a14040] hover:text-red-800"
                          title="Eliminar fila"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr data-testid={CAPTURE.totalsRow}>
                  <td className="ajar-cell-total" colSpan={3}>
                    TOTALES
                  </td>
                  <td className="ajar-cell-total text-right">$ {fmt(totals.tithes)}</td>
                  <td className="ajar-cell-total text-right">$ {fmt(totals.offering)}</td>
                  <td className="ajar-cell-total text-right">
                    $ {fmt(totals.special_offering)}
                  </td>
                  <td className="ajar-cell-total text-right">{totals.attendance_adults}</td>
                  <td className="ajar-cell-total text-right">{totals.attendance_youth}</td>
                  <td className="ajar-cell-total text-right">{totals.attendance_kids}</td>
                  <td className="ajar-cell-total text-right">$ {fmt(totalEntradas)}</td>
                  <td className="ajar-cell-total" colSpan={3}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        <div className="text-xs text-[#8b7e5e] flex items-center gap-4 flex-wrap">
          <Legend swatch="#d6ebcb" label="Domingo" />
          <Legend swatch="#e3e3e3" label="Jueves" />
          <span>•</span>
          <span>
            <strong>Diezmos</strong> se calcula automáticamente a partir de los{" "}
            <strong>Sobres</strong> registrados para cada servicio.
          </span>
        </div>
      </div>

      <EnvelopesModal
        open={!!envelopeRow}
        onClose={handleEnvelopesClose}
        capture={envelopeRow}
        members={members}
        onApplyTotals={applyEnvelopeTotal}
      />
    </div>
  );
}

const selectCls =
  "bg-white border border-[#d9cfb8] px-3 py-2 text-sm text-[#1c1a14] focus:outline-none focus:border-[#c8a04a]";

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-[#8b7e5e] mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="text-right">
      <div className="text-[10px] uppercase tracking-widest text-[#8b7e5e]">{label}</div>
      <div className="font-serif text-xl mt-0.5" style={{ color: accent }}>
        {value}
      </div>
    </div>
  );
}

// Read-only Tithes cell: auto-computed from envelopes for that capture.
function TithesCell({ value, testid, hasId }) {
  return (
    <td className="text-right">
      <div
        data-testid={testid}
        title={
          hasId
            ? "Diezmos se calcula automáticamente desde los Sobres del servicio"
            : "Guarda la fila primero para registrar Sobres"
        }
        className="ajar-input text-right tabular-nums font-medium bg-[#f5f0e6] text-[#1c2a3a] cursor-not-allowed select-none flex items-center justify-end gap-1.5"
        aria-readonly="true"
      >
        <Lock size={11} className="text-[#8b7e5e]" />
        <span>$ {fmt(value)}</span>
      </div>
    </td>
  );
}

// Currency cell: type=text, no spinners, displays "$ 1,234.56" when not focused,
// raw editable number when focused. Stores raw float via onChange.
function CurrencyCell({ value, onChange, testid }) {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef(null);

  const numericVal = Number(value) || 0;
  const display = focused
    ? draft
    : numericVal === 0
    ? ""
    : `$ ${fmt(numericVal)}`;

  const handleFocus = (e) => {
    setFocused(true);
    setDraft(numericVal === 0 ? "" : String(numericVal));
    requestAnimationFrame(() => e.target?.select?.());
  };

  const handleChange = (e) => {
    // Allow digits and a single dot
    let v = e.target.value.replace(/[^0-9.]/g, "");
    const firstDot = v.indexOf(".");
    if (firstDot !== -1) {
      v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, "");
    }
    setDraft(v);
  };

  const handleBlur = () => {
    setFocused(false);
    const parsed = parseFloat(draft);
    onChange(Number.isFinite(parsed) ? parsed : 0);
  };

  return (
    <td className="text-right">
      <input
        ref={inputRef}
        data-testid={testid}
        type="text"
        inputMode="decimal"
        value={display}
        onFocus={handleFocus}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder="$ 0.00"
        className="ajar-input text-right tabular-nums"
      />
    </td>
  );
}

// Integer cell with spinners (kept for Adultos/Jóvenes/Niños).
function NumCell({ value, onChange, testid, integer = false }) {
  return (
    <td className="text-right">
      <input
        data-testid={testid}
        type="number"
        step={integer ? "1" : "0.01"}
        min="0"
        value={value === 0 ? "" : value}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") return onChange(0);
          const n = integer ? parseInt(raw, 10) : parseFloat(raw);
          onChange(Number.isFinite(n) ? n : 0);
        }}
        onFocus={(e) => e.target.select()}
        placeholder="0"
        className="ajar-input text-right tabular-nums"
      />
    </td>
  );
}

function Legend({ swatch, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block w-3 h-3 border border-[#d9cfb8]"
        style={{ background: swatch }}
      />
      {label}
    </span>
  );
}
